import { ResourceHelperService } from "../resource-helper.service";

/**
 * A service that provides access to catalogue service resources
 */
export class CatalogueService {
  private resource: InterACT.Interfaces.IResource<InterACT.Interfaces.Catalogues.CatalogueResponse | InterACT.Interfaces.Catalogues.CataloguesResponse>;
  private authorisationRequest: InterACT.Interfaces.IResource<InterACT.Interfaces.Catalogues.AuthorisationRequest>;
  private authorisationApprovalResource: InterACT.Interfaces.IResource<InterACT.Interfaces.Catalogues.ApprovalRequest>;

  constructor(
    private $resource: ng.resource.IResourceService,
    private resourceHelperService: ResourceHelperService) {
    this.resource = $resource(
      'api/catalogues/:catalogueId',
      undefined,
      { edit: { method: 'PATCH' }, update: { method: 'PUT' } });
    this.authorisationRequest = $resource('api/catalogues/:catalogueId/approval-requests');
    this.authorisationApprovalResource = $resource('api/catalogues/:catalogueId/authorisation-requests');
  }

  /**
   * Gets a list of catalogues
   * @param page the page to get catalogues for
   * @param perPageCount the number of catalogues per page
   * @param searchText the query we wish to use for this request
   * @param expandCatalogue a boolean that represents if we wish to retreive an expanded catalogue with all of its products
   * @returns a promise of an ICatalogueServiceResponse
   */
  public getList(page?: number, perPageCount?: number, expandCatalogue?: boolean, schemes?: Array<string>) {
    const options = this.resourceHelperService.buildRequestOptions(page, perPageCount);

    options.expand = expandCatalogue ? expandCatalogue : false;
    if (schemes) {
      options.schemes = schemes;
    }
    return <ng.IPromise<InterACT.Interfaces.Catalogues.CataloguesResponse>>this.resource.get(options).$promise;
  }

  /**
   * Gets an individual catalogue by its id
   * @param catalogueId the id of the catalogue to retrieve
   * @param options a list of options for the request
   * @returns a promise of an ICatalogueResponse or Catalogue
   */
  public getCatalogue(catalogueId?: string, options?: any) {
    if (angular.isObject(options) && !options.hasOwnProperty('includeValidation')) {
      options.includeValidation = false;
    } else if (!angular.isObject(options)) {
      options = {
        includeValidation: false
      };
    }
    options.catalogueId = catalogueId;
    return this.resource.get(options).$promise.then((resource) => {
      return options.includeValidation ? resource : (<InterACT.Interfaces.Catalogues.CatalogueResponse>resource).catalogue;
    });
  }

  /**
   * Gets an expanded catalogue
   * @param catalogueId The id of the catalogue we wish to retrieve
   * @param searchText The query we wish to use for this request
   * @returns a promise of an ICatalogueServiceResponse
   */
  public getExpandedCatalogue(catalogueId?: string, searchText?: string) {
    const options = this.resourceHelperService.buildRequestOptions(undefined, undefined, searchText);

    options.expand = true;
    options.catalogueId = catalogueId;

    return this.resource.get(options).$promise;
  }

  /**
   * Creates a catalogue with the data provided
   * @param data the data to create
   * @returns a promise of an ICatalogueServiceResponse
   */
  public create(data: InterACT.Interfaces.Catalogues.Catalogue) {
    return this.resource.save(data).$promise;
  }

  /**
   * Edits a catalogue with the id entered using the data provided
   * @param catalogueId the id of the catalogue
   * @param data the data to update the catalogue
   * @returns a promise of an ICatalogueServiceResponse
   */
  public edit(catalogueId: string, data: InterACT.Interfaces.Catalogues.Catalogue) {
    return (<any>this.resource).edit({ catalogueId: catalogueId }, data).$promise;
  }

  /**
   * Sends Authorisation Request for the catalogue whos id was entered
   * @param catalogueId the id of the catalogue
   * @param catalogue the catalogue data to authorise
   * @returns a promise of a response
   */
  public requestAuthorisation(catalogueId: string, catalogue: InterACT.Interfaces.Catalogues.Catalogue) {
    return this.authorisationRequest.save({ catalogueId: catalogueId }, catalogue).$promise;
  }

  /**
   * Sends authorisation approval for the catalogue whos id was entered
   * @param catalogueId the id of the catalogue
   * @param catalogue the catalogue data to authorise
   * @returns a promise of a response
   */
  public authorisationApproval(catalogueId: string, catalogue: InterACT.Interfaces.Catalogues.Catalogue) {
    return this.authorisationApprovalResource.save({ catalogueId: catalogueId }, catalogue).$promise;
  }

  /**
   * Removes a catalogue
   * @param catalogueId the id of the catalogue to remove
   * @returns a promise of a response
   */
  public remove(catalogueId: string) {
    return this.resource.delete({ catalogueId: catalogueId }).$promise;
  }

  /**
   * Copies the data of a catalogue into a new create action
   * @param data the data for the copied catalogue
   * @returns a promise of an ICatalogueServiceResponse
   */
  public copy(data: InterACT.Interfaces.Catalogues.Catalogue) {
    if (data.effectiveFrom === undefined) {
      delete data.effectiveFrom;
    }

    delete data.id;
    return this.create(data);
  }
}
